import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building, MapPin, ImageOff, Paperclip, Link, X, Plus, FileText, Upload, Download, Info, Target, BarChart3, Leaf, Users, Mail, Phone, ChevronDown, ChevronUp, Briefcase, Eye, Shield, Zap } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { validateFileSize, getUploadErrorMessage } from '../lib/uploadUtils';
import { downloadSecureFile, openSecureFile } from '../lib/secureFileAccess';
import { useAutoSave, AutoSaveStatus } from '../hooks/useAutoSave';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { ModulePageHeader } from '../components/ModulePageHeader';
import { OrganizationOperationalData } from '../components/organization/OrganizationOperationalData';
import { SecureFileImage } from '../components/SecureFileImage';
import {
  createDraftEvidence,
  deleteEvidenceFiles,
  draftEvidence,
  getEvidenceFileId,
  persistedEvidence,
  revokeDraftEvidence,
  revokeDraftEvidences,
  toEvidencePayload,
  uploadDraftEvidences,
} from '../lib/draftEvidence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to ensure URL has proper protocol for external links
const ensureProtocol = (url) => {
  if (!url) return url;
  // If it's an internal API URL or already has a protocol, return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/')) {
    return url;
  }
  // Add https:// to external URLs
  return `https://${url}`;
};

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Germany', 'France', 'Australia', 
  'Canada', 'Japan', 'China', 'Brazil', 'European Union', 'Other'
];

export default function OrganizationDetails() {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [facilityCount, setFacilityCount] = useState(0);
  const [pendingLogo, setPendingLogo] = useState(null);
  const pendingDeletionIds = useRef(new Set());
  
  // Collapsible text states
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  
  const { getAuthHeader, user, subscriptionExpired } = useAuth();
  const { hasAccess, loading: moduleAccessLoading } = useModuleAccess();

  const showGhgDetails = !moduleAccessLoading && hasAccess('environment.ghg');
  const showOperationalData = !moduleAccessLoading && [
    'environment',
    'social',
    'governance'
  ].some(hasAccess);

  useEffect(() => {
    const activeTabIsAvailable = activeTab === 'basic'
      || (activeTab === 'ghg' && showGhgDetails)
      || (['production', 'revenue'].includes(activeTab) && showOperationalData);

    if (!activeTabIsAvailable) {
      setActiveTab('basic');
    }
  }, [activeTab, showGhgDetails, showOperationalData]);

  // Check if user is Admin (can edit) or User (read-only)
  // Also block editing if subscription is expired
  const canEdit = user?.role === 'admin' && !subscriptionExpired;
  
  const validatePincode = (value) => {
    if (value && (!/^\d{6}$/.test(value))) {
      setPincodeError('Pincode must be exactly 6 digits');
      return false;
    }
    setPincodeError('');
    return true;
  };
  
  const handlePincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setFormData({ ...formData, pincode: cleaned });
    if (cleaned) validatePincode(cleaned);
    else setPincodeError('');
  };

  const [formData, setFormData] = useState({
    name: '',
    corporate_address: '',
    city: '',
    state: '',
    country: '',
    timezone: '',  // IANA timezone string
    pincode: '',
    logo: '',
    general_description: '',
    mission: '',
    vision: '',
    process_description: '',
    org_boundaries_approach: 'control',
    org_boundaries_equity_percentage: '',  // Legacy field - kept for backward compatibility
    org_boundaries: '',
    equity_share_reported_data_type: '',  // "org_share" or "total_facility"
    control_types: [],  // Array for multiple control types: ["operational", "financial"]
    uncertainty_assessment: [],  // Array of selected uncertainty assessment options
    other_information: '',
    reporting_frequency: 'yearly',
    reporting_year_type: '',  // "financial_year" or "calendar_year"
    attachments: [],
    // New fields
    person_responsible: '',
    person_responsible_designation: '',
    person_responsible_contact: '',
    report_purpose: '',
    ghg_reduction_initiatives: '',
    internal_performance_tracking: ''
  });
  
  const [timezones, setTimezones] = useState([]);  // Available timezone options

  const [newAttachment, setNewAttachment] = useState({ name: '', url: '' });

  // Helper function to download files
  const downloadFile = (url, filename) => {
    window.location.href = url;
  };

  // Validation function for auto-save - checks all mandatory fields
  const validateOrganizationForm = useCallback((data) => {
    // Check all mandatory fields
    if (!data.corporate_address || data.corporate_address.trim() === '') return false;
    if (!data.city || data.city.trim() === '') return false;
    if (!data.state || data.state.trim() === '') return false;
    if (!data.country || data.country.trim() === '') return false;
    if (!data.pincode || data.pincode.trim() === '') return false;
    if (data.pincode && !/^\d{6}$/.test(data.pincode)) return false;
    if (!data.person_responsible || data.person_responsible.trim() === '') return false;
    if (!data.reporting_year_type) return false;
    
    return true;
  }, []);

  // Auto-save handler for organization
  const handleAutoSave = useCallback(async (data) => {
    // Prepare data, converting empty strings to null for optional fields
    const submitData = {
      ...data,
      reporting_frequency: data.reporting_frequency || 'yearly',
      reporting_year_type: data.reporting_year_type,
      org_boundaries_equity_percentage: data.org_boundaries_equity_percentage 
        ? parseFloat(data.org_boundaries_equity_percentage) 
        : null,
      org_boundaries_approach: data.org_boundaries_approach || null,
      org_boundaries: data.org_boundaries || null,
      equity_share_reported_data_type: data.equity_share_reported_data_type || null,
      control_types: data.control_types || [],
      uncertainty_assessment: data.uncertainty_assessment || [],
      other_information: data.other_information || null,
      person_responsible: data.person_responsible || null,
      person_responsible_designation: data.person_responsible_designation || null,
      person_responsible_contact: data.person_responsible_contact || null,
      report_purpose: data.report_purpose || null,
      ghg_reduction_initiatives: data.ghg_reduction_initiatives || null,
      internal_performance_tracking: data.internal_performance_tracking || null,
      general_description: data.general_description || null,
      mission: data.mission || null,
      vision: data.vision || null,
      process_description: data.process_description || null,
      city: data.city || null,
      state: data.state || null,
      country: data.country || null,
      timezone: data.timezone || null,
      pincode: data.pincode || null,
      logo: organization?.logo || null,
      attachments: organization?.attachments || [],
    };
    
    await axios.put(`${API}/organizations/my`, submitData, {
      headers: getAuthHeader()
    });
    
    return { id: organization?.id };
  }, [getAuthHeader, organization?.attachments, organization?.id, organization?.logo]);

  // Auto-save hook
  const { 
    saveStatus, 
    lastSavedAt, 
    errorMessage,
    triggerSave: triggerAutoSave,
    resetAutoSave 
  } = useAutoSave({
    onSave: handleAutoSave,
    validate: validateOrganizationForm,
    formData,
    enabled: editing && canEdit,
    inactivityMs: 5 * 60 * 1000, // 5 minutes
    isEditing: true, // Organization always exists
    existingId: organization?.id
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  // Fetch available timezones on component mount
  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        const response = await axios.get(`${API}/timezones`, { headers: getAuthHeader() });
        setTimezones(response.data || []);
      } catch (error) {
        console.error('Failed to fetch timezones:', error);
      }
    };
    fetchTimezones();
  }, [getAuthHeader]);

  // Update timezone when country changes (suggest default)
  const handleCountryChange = async (country) => {
    setFormData(prev => ({ ...prev, country }));
    
    // If no timezone set yet, fetch the default for this country
    if (country && !formData.timezone) {
      try {
        const response = await axios.get(`${API}/timezones/default/${encodeURIComponent(country)}`, { headers: getAuthHeader() });
        if (response.data?.timezone) {
          setFormData(prev => ({ ...prev, timezone: response.data.timezone }));
        }
      } catch (error) {
        console.error('Failed to get default timezone:', error);
      }
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/my`, {
        headers: getAuthHeader()
      });
      setOrganization(response.data);
      
      // Reconstruct control_types from org_boundaries_approach
      let controlTypes = response.data.control_types || [];
      if (controlTypes.length === 0 && response.data.org_boundaries_approach) {
        if (response.data.org_boundaries_approach === 'control_both') {
          controlTypes = ['operational', 'financial'];
        } else if (response.data.org_boundaries_approach === 'control_operational') {
          controlTypes = ['operational'];
        } else if (response.data.org_boundaries_approach === 'control_financial') {
          controlTypes = ['financial'];
        }
      }
      
      // Keep the protected file route; SecureFileImage resolves it through an authenticated request.
      const logoUrl = response.data.logo || '';
      
      setFormData({
        name: response.data.name,
        corporate_address: response.data.corporate_address,
        city: response.data.city || '',
        state: response.data.state || '',
        country: response.data.country || '',
        timezone: response.data.timezone || '',
        pincode: response.data.pincode || '',
        logo: logoUrl,
        general_description: response.data.general_description || '',
        mission: response.data.mission || '',
        vision: response.data.vision || '',
        process_description: response.data.process_description || '',
        org_boundaries_approach: response.data.org_boundaries_approach || 'control',
        org_boundaries_equity_percentage: response.data.org_boundaries_equity_percentage || '',
        org_boundaries: response.data.org_boundaries || '',
        equity_share_reported_data_type: response.data.equity_share_reported_data_type || '',
        control_types: controlTypes,
        uncertainty_assessment: response.data.uncertainty_assessment || [],
        other_information: response.data.other_information || response.data.remarks || '',
        reporting_frequency: response.data.reporting_frequency || 'yearly',
        reporting_year_type: response.data.reporting_year_type || '',
        attachments: response.data.attachments || [],
        // New fields
        person_responsible: response.data.person_responsible || '',
        person_responsible_designation: response.data.person_responsible_designation || '',
        person_responsible_contact: response.data.person_responsible_contact || '',
        report_purpose: response.data.report_purpose || '',
        ghg_reduction_initiatives: response.data.ghg_reduction_initiatives || '',
        internal_performance_tracking: response.data.internal_performance_tracking || ''
      });
    } catch (error) {
      console.error('Organization fetch error:', error);
      if (error.response?.status === 404) {
        toast.error('No organization assigned to your account');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilityCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/facilities`, {
        headers: getAuthHeader(),
      });
      const facilities = Array.isArray(response.data)
        ? response.data
        : response.data?.facilities || [];
      setFacilityCount(facilities.length);
    } catch (error) {
      console.error('Failed to load facility count:', error);
      setFacilityCount(0);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (organization && !editing) {
      fetchFacilityCount();
    }
  }, [editing, fetchFacilityCount, organization]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo file size should be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const sizeErr = validateFileSize(file);
    if (sizeErr) {
      toast.error(sizeErr);
      setUploadingLogo(false);
      return;
    }
    
    try {
      revokeDraftEvidence(pendingLogo);
      setPendingLogo(createDraftEvidence(file, { type: 'logo' }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error(getUploadErrorMessage(error, file));
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = () => {
    if (pendingLogo) {
      revokeDraftEvidence(pendingLogo);
      setPendingLogo(null);
      return;
    }
    const fileId = getEvidenceFileId({ url: formData.logo });
    if (fileId) pendingDeletionIds.current.add(fileId);
    setFormData(prev => ({ ...prev, logo: '' }));
  };

  const addAttachment = () => {
    if (!newAttachment.name || !newAttachment.url) {
      toast.error('Please provide both name and URL');
      return;
    }
    setFormData({
      ...formData,
      attachments: [...formData.attachments, { ...newAttachment }]
    });
    setNewAttachment({ name: '', url: '' });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newAttachments = [];

    for (const file of files) {
      const sizeErr = validateFileSize(file);
      if (sizeErr) {
        toast.error(sizeErr);
        continue;
      }

      newAttachments.push(createDraftEvidence(file, { type: 'file' }));
    }

    if (newAttachments.length > 0) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments]
      }));
      toast.success(`${newAttachments.length} file(s) uploaded`);
    }
    
    e.target.value = '';
  };

  const removeAttachment = async (index) => {
    const attachment = formData.attachments[index];
    if (attachment?.is_draft) revokeDraftEvidence(attachment);
    const fileId = getEvidenceFileId(attachment);
    if (!attachment?.is_draft && fileId) pendingDeletionIds.current.add(fileId);
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    toast.success('Attachment removed');
  };

  const discardFileChanges = () => {
    revokeDraftEvidence(pendingLogo);
    revokeDraftEvidences(draftEvidence(formData.attachments));
    setPendingLogo(null);
    pendingDeletionIds.current.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Address Details are mandatory
    if (!formData.corporate_address || formData.corporate_address.trim() === '') {
      toast.error('Corporate Address is mandatory');
      return;
    }
    if (!formData.city || formData.city.trim() === '') {
      toast.error('City is mandatory');
      return;
    }
    if (!formData.state || formData.state.trim() === '') {
      toast.error('State is mandatory');
      return;
    }
    if (!formData.country || formData.country.trim() === '') {
      toast.error('Country is mandatory');
      return;
    }
    if (!formData.pincode || formData.pincode.trim() === '') {
      toast.error('Pincode is mandatory');
      return;
    }
    
    // Validation: Person Responsible is mandatory
    if (!formData.person_responsible || formData.person_responsible.trim() === '') {
      toast.error('Person Responsible is mandatory');
      return;
    }
    
    // Validate Reporting Year Type is selected
    if (!formData.reporting_year_type) {
      toast.error('Please select a Reporting Year Type');
      return;
    }
    
    try {
      // Prepare data, converting empty strings to null for optional fields
      const persistedAttachments = persistedEvidence(formData.attachments).map(toEvidencePayload);
      const submitData = {
        ...formData,
        attachments: persistedAttachments,
        reporting_frequency: formData.reporting_frequency || 'yearly',
        reporting_year_type: formData.reporting_year_type,
        // Convert empty strings to null for optional numeric fields
        org_boundaries_equity_percentage: formData.org_boundaries_equity_percentage 
          ? parseFloat(formData.org_boundaries_equity_percentage) 
          : null,
        // Convert empty strings to null for optional text fields
        org_boundaries_approach: formData.org_boundaries_approach || null,
        org_boundaries: formData.org_boundaries || null,
        equity_share_reported_data_type: formData.equity_share_reported_data_type || null,
        control_types: formData.control_types || [],
        uncertainty_assessment: formData.uncertainty_assessment || [],
        other_information: formData.other_information || null,
        person_responsible: formData.person_responsible || null,
        person_responsible_designation: formData.person_responsible_designation || null,
        person_responsible_contact: formData.person_responsible_contact || null,
        report_purpose: formData.report_purpose || null,
        ghg_reduction_initiatives: formData.ghg_reduction_initiatives || null,
        internal_performance_tracking: formData.internal_performance_tracking || null,
        general_description: formData.general_description || null,
        mission: formData.mission || null,
        vision: formData.vision || null,
        process_description: formData.process_description || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        pincode: formData.pincode || null,
        logo: formData.logo || null
      };
      
      await axios.put(`${API}/organizations/my`, submitData, {
        headers: getAuthHeader()
      });

      const stagedAttachments = draftEvidence(formData.attachments);
      const attachmentsResult = await uploadDraftEvidences({
        items: stagedAttachments,
        uploadUrl: `${API}/upload/evidence?bucket_type=org_facility&organization_id=${organization?.id || ''}`,
        headers: getAuthHeader(),
        mapUploaded: (uploaded, draft) => ({
          type: 'file',
          name: draft.name,
          url: draft.content_type?.startsWith('image/') ? `${uploaded.url}/view` : uploaded.url,
          file_id: uploaded.file_id,
        }),
      });
      const logoResult = pendingLogo ? await uploadDraftEvidences({
        items: [pendingLogo],
        uploadUrl: `${API}/upload/evidence?bucket_type=org_facility&organization_id=${organization?.id || ''}`,
        headers: getAuthHeader(),
        mapUploaded: (uploaded) => ({ url: `${uploaded.url}/view`, file_id: uploaded.file_id }),
      }) : { uploaded: [], failed: [] };

      const uploadedFiles = [...attachmentsResult.uploaded, ...logoResult.uploaded];
      const finalAttachments = [...persistedAttachments, ...attachmentsResult.uploaded];
      const finalLogo = logoResult.uploaded[0]?.url || submitData.logo;
      if (uploadedFiles.length > 0) {
        try {
          await axios.put(`${API}/organizations/my`, {
            ...submitData,
            attachments: finalAttachments,
            logo: finalLogo,
          }, { headers: getAuthHeader() });
        } catch (error) {
          await deleteEvidenceFiles(uploadedFiles.map(getEvidenceFileId), API, getAuthHeader());
          throw error;
        }
      }

      if (logoResult.uploaded.length > 0) {
        const previousLogoId = getEvidenceFileId({ url: organization?.logo });
        if (previousLogoId) pendingDeletionIds.current.add(previousLogoId);
      }
      const deletionFailures = await deleteEvidenceFiles(pendingDeletionIds.current, API, getAuthHeader());
      pendingDeletionIds.current.clear();
      const uploadFailures = attachmentsResult.failed.length + logoResult.failed.length;
      if (uploadFailures || deletionFailures) {
        toast.error(`Organization saved, but ${uploadFailures + deletionFailures} file change(s) could not be completed.`);
      }
      discardFileChanges();
      toast.success('Organization updated successfully');
      setEditing(false);
      fetchOrganization();
    } catch (error) {
      console.error('Organization update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <Building className="w-16 h-16 mx-auto text-text-muted mb-4" />
        <h2 className="text-xl font-medium text-text-primary mb-2">No Organization Assigned</h2>
        <p className="text-text-muted">Please contact your administrator to be assigned to an organization.</p>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      <ModulePageHeader
        title="Organization"
        icon={Building}
        iconClassName="border-blue-200 bg-blue-50 text-blue-700"
        testId="organization"
        aside={user?.role === 'admin' && !editing && (
          <Button 
            onClick={() => {
              if (subscriptionExpired) {
                toast.error('Your subscription has expired. Please contact your administrator to renew.');
                return;
              }
              setActiveTab('basic');
              setEditing(true);
            }} 
            className="bg-primary hover:bg-primary/90 text-white rounded-full px-6" 
            data-testid="edit-org-btn"
            disabled={subscriptionExpired}
          >
            Edit Details
          </Button>
        )}
      />

      {/* ========== PERSISTENT ORGANIZATION SUMMARY HEADER ========== */}
      {!editing && (
        <Card className="p-0 border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-primary via-emerald-500 to-teal-500" />
          <div className="p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-4">
              <SecureFileImage source={organization?.logo} alt={`${organization?.name || 'Organization'} logo`} className="h-20 w-20 rounded-xl border-2 border-stone-100 bg-white object-contain shadow-sm" testId="organization-summary-logo" fallback={<div className="flex h-20 w-20 items-center justify-center rounded-xl border border-stone-100 bg-gradient-to-br from-primary/10 to-emerald-50" data-testid="organization-summary-logo-fallback">
                  <Building className="h-10 w-10 text-primary" />
                </div>} />
              <div className="min-w-0 flex-1 self-center">
                <h1 className="break-words font-heading text-2xl font-bold text-text-primary lg:text-3xl" data-testid="organization-summary-name">
                  {organization?.name}
                </h1>
              </div>
              </div>
              <div className="self-start rounded-lg bg-stone-50 px-4 py-2 text-center sm:self-auto" data-testid="organization-facility-count">
                <div className="text-2xl font-bold text-primary">{facilityCount}</div>
                <div className="text-xs text-text-muted">No. of Facilities</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Framework Tabs - with increased top spacing */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="relative flex h-auto w-full max-w-full justify-start overflow-hidden rounded-full bg-stone-100 p-1 sm:w-auto sm:overflow-x-auto">
          <TabsTrigger 
            value="basic" 
            className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-full px-2 text-[10px] text-stone-600 transition-colors duration-200 hover:text-emerald-900 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none sm:flex-none sm:px-6 sm:text-sm"
            data-testid="organization-tab-basic"
          >
            {activeTab === 'basic' && <motion.span layoutId="organization-details-tab-indicator" className="pointer-events-none absolute inset-0 rounded-full bg-emerald-800 shadow-sm" transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.55 }} aria-hidden="true" />}
            <span className="relative z-10 whitespace-nowrap">Basic Details</span>
          </TabsTrigger>
          {showGhgDetails && (
            <TabsTrigger
              value="ghg"
              className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-full px-2 text-[10px] text-stone-600 transition-colors duration-200 hover:text-emerald-900 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none sm:flex-none sm:px-6 sm:text-sm"
              data-testid="organization-tab-ghg"
            >
              {activeTab === 'ghg' && <motion.span layoutId="organization-details-tab-indicator" className="pointer-events-none absolute inset-0 rounded-full bg-emerald-800 shadow-sm" transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.55 }} aria-hidden="true" />}
              <span className="relative z-10 whitespace-nowrap">GHG Details</span>
            </TabsTrigger>
          )}
          {showOperationalData && (
            <>
              <TabsTrigger
                value="production"
                className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-full px-2 text-[10px] text-stone-600 transition-colors duration-200 hover:text-emerald-900 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none sm:flex-none sm:px-6 sm:text-sm"
                data-testid="organization-tab-production"
              >
                {activeTab === 'production' && <motion.span layoutId="organization-details-tab-indicator" className="pointer-events-none absolute inset-0 rounded-full bg-emerald-800 shadow-sm" transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.55 }} aria-hidden="true" />}
                <span className="relative z-10 whitespace-nowrap">Production Data</span>
              </TabsTrigger>
              <TabsTrigger
                value="revenue"
                className="relative z-0 min-w-0 flex-1 overflow-hidden rounded-full px-2 text-[10px] text-stone-600 transition-colors duration-200 hover:text-emerald-900 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none sm:flex-none sm:px-6 sm:text-sm"
                data-testid="organization-tab-revenue"
              >
                {activeTab === 'revenue' && <motion.span layoutId="organization-details-tab-indicator" className="pointer-events-none absolute inset-0 rounded-full bg-emerald-800 shadow-sm" transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.55 }} aria-hidden="true" />}
                <span className="relative z-10 whitespace-nowrap">Revenue Data</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <div className="mt-2">
          {editing ? (
            <Card className={`p-6 border border-stone-200 rounded-xl bg-white ${['production', 'revenue'].includes(activeTab) ? 'hidden' : ''}`}>
              <form onSubmit={handleSubmit} className="space-y-4">
            <div className={activeTab === 'basic' ? 'space-y-4' : 'hidden'}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organization Name (Read-only)</Label>
                <Input value={formData.name} disabled className="bg-stone-100" />
              </div>
              <div className="space-y-2">
                <Label>Logo (Upload)</Label>
                <div className="flex items-center gap-4">
                  <SecureFileImage source={pendingLogo?.preview_url || formData.logo} alt="Logo preview" className="w-16 h-16 object-contain border border-stone-200 rounded-lg" testId="organization-logo-preview" fallback={<div className="w-16 h-16 flex items-center justify-center border border-stone-200 rounded-lg bg-stone-100" data-testid="organization-logo-preview-fallback">
                      <ImageOff className="w-6 h-6 text-stone-400" />
                    </div>} />
                  <div className="flex-1">
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => document.getElementById('logo-upload')?.click()}
                      disabled={uploadingLogo}
                      data-testid="organization-logo-upload-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? 'Staging...' : 'Upload Logo'}
                    </Button>
                    {(pendingLogo || formData.logo) && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        className="ml-2 text-accent"
                        onClick={handleRemoveLogo}
                        data-testid="organization-logo-remove-button"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="p-4 border border-stone-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <MapPin className="w-4 h-4" />
                Corporate Address Details <span className="text-red-500">*</span>
              </div>
              <div className="space-y-2">
                <Label>Street Address <span className="text-red-500">*</span></Label>
                <Input 
                  value={formData.corporate_address} 
                  onChange={(e) => setFormData({ ...formData, corporate_address: e.target.value })} 
                  className="bg-stone-50" 
                  placeholder="Enter street address"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City <span className="text-red-500">*</span></Label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="bg-stone-50" required />
                </div>
                <div className="space-y-2">
                  <Label>State/Province <span className="text-red-500">*</span></Label>
                  <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="bg-stone-50" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country <span className="text-red-500">*</span></Label>
                  <select value={formData.country} onChange={(e) => handleCountryChange(e.target.value)} className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3" required>
                    <option value="">Select Country</option>
                    {COUNTRIES.map(c => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <select 
                    value={formData.timezone} 
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} 
                    className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3"
                  >
                    <option value="">Select Timezone</option>
                    {timezones.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PIN/ZIP Code <span className="text-red-500">*</span></Label>
                  <Input 
                    value={formData.pincode} 
                    onChange={(e) => handlePincodeChange(e.target.value)} 
                    maxLength={6}
                    placeholder="6-digit pincode"
                    className={`bg-stone-50 ${pincodeError ? 'border-red-500' : ''}`}
                    required
                  />
                  {pincodeError && <p className="text-xs text-red-500">{pincodeError}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Organization Description</Label>
              <textarea value={formData.general_description} onChange={(e) => setFormData({ ...formData, general_description: e.target.value })} rows={3} className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mission</Label>
                <textarea value={formData.mission} onChange={(e) => setFormData({ ...formData, mission: e.target.value })} rows={2} className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
              </div>
              <div className="space-y-2">
                <Label>Vision</Label>
                <textarea value={formData.vision} onChange={(e) => setFormData({ ...formData, vision: e.target.value })} rows={2} className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Process Description</Label>
              <textarea value={formData.process_description} onChange={(e) => setFormData({ ...formData, process_description: e.target.value })} rows={3} className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
            </div>

            {/* Person Responsible */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>Person Responsible <span className="text-red-500">*</span></Label>
                <Input 
                  value={formData.person_responsible} 
                  onChange={(e) => setFormData({ ...formData, person_responsible: e.target.value })} 
                  className="bg-stone-50"
                  placeholder="Name of person responsible for GHG reporting"
                  required
                  data-testid="organization-person-responsible-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input 
                  value={formData.person_responsible_designation || ''} 
                  onChange={(e) => setFormData({ ...formData, person_responsible_designation: e.target.value })} 
                  className="bg-stone-50"
                  placeholder="e.g., Sustainability Director"
                  data-testid="organization-person-responsible-designation-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Details</Label>
                <Input 
                  value={formData.person_responsible_contact || ''} 
                  onChange={(e) => setFormData({ ...formData, person_responsible_contact: e.target.value })} 
                  className="bg-stone-50"
                  placeholder="Email or phone number"
                  data-testid="organization-person-responsible-contact-input"
                />
              </div>
            </div>
            </div>

            <div className={activeTab === 'ghg' ? 'space-y-4' : 'hidden'}>
            {/* Purpose of the GHG Report */}
            <div className="space-y-2">
              <Label>Purpose of the GHG Report</Label>
              <textarea 
                value={formData.report_purpose} 
                onChange={(e) => setFormData({ ...formData, report_purpose: e.target.value })} 
                rows={2} 
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" 
                placeholder="Describe the purpose of the GHG inventory report"
              />
            </div>

            <div className="space-y-2">
              <Label>Reporting Frequency</Label>
              <select
                value={formData.reporting_frequency}
                onChange={(e) => setFormData({ ...formData, reporting_frequency: e.target.value })}
                className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3"
                data-testid="organization-reporting-frequency-select"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            {/* Organizational Boundaries */}
            <div className="p-4 border border-stone-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-base font-semibold">Organizational Boundaries</Label>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>It defines which operations and facilities are included in the GHG inventory based on the selected consolidation approach (Equity Share or Control Approach). This helps clarify how emissions are attributed and accounted for within the organization.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <input 
                    type="radio" 
                    id="control_approach" 
                    name="org_boundaries_approach" 
                    value="control"
                    checked={formData.org_boundaries_approach === 'control' || formData.org_boundaries_approach === 'control_operational' || formData.org_boundaries_approach === 'control_financial' || formData.org_boundaries_approach === 'control_both'}
                    onChange={(e) => setFormData({ ...formData, org_boundaries_approach: e.target.value, org_boundaries_equity_percentage: '' })}
                    className="mt-1"
                  />
                  <label htmlFor="control_approach" className="text-sm">
                    <span className="font-medium">Control Approach</span>
                    <p className="text-text-muted mt-1">Under this approach, a company considers and accounts for 100% of the greenhouse gas emissions from operations over which it has either operational or financial control. It does not report the GHG emissions from those operations in which it has no control.</p>
                  </label>
                </div>

                {/* Sub-options for Control Approach - Now allows multiple selection */}
                {(formData.org_boundaries_approach === 'control' || formData.org_boundaries_approach === 'control_operational' || formData.org_boundaries_approach === 'control_financial' || formData.org_boundaries_approach === 'control_both') && (
                  <div className="ml-8 space-y-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
                    <Label className="text-sm font-medium">Select Control Type(s) <span className="text-text-muted font-normal">(Optional)</span></Label>
                    <p className="text-xs text-text-muted mb-2">You can select one or both options</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="control_operational" 
                          checked={formData.control_types?.includes('operational')}
                          onChange={(e) => {
                            const newTypes = e.target.checked 
                              ? [...(formData.control_types || []), 'operational']
                              : (formData.control_types || []).filter(t => t !== 'operational');
                            setFormData({ 
                              ...formData, 
                              control_types: newTypes,
                              org_boundaries_approach: newTypes.length === 2 ? 'control_both' : 
                                newTypes.includes('operational') ? 'control_operational' : 
                                newTypes.includes('financial') ? 'control_financial' : 'control'
                            });
                          }}
                          className="h-4 w-4 rounded border-stone-300"
                        />
                        <label htmlFor="control_operational" className="text-sm cursor-pointer">
                          <span className="font-medium">Operational Control</span>
                          <span className="text-text-muted ml-1">- Full authority to implement operating policies</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="control_financial" 
                          checked={formData.control_types?.includes('financial')}
                          onChange={(e) => {
                            const newTypes = e.target.checked 
                              ? [...(formData.control_types || []), 'financial']
                              : (formData.control_types || []).filter(t => t !== 'financial');
                            setFormData({ 
                              ...formData, 
                              control_types: newTypes,
                              org_boundaries_approach: newTypes.length === 2 ? 'control_both' : 
                                newTypes.includes('operational') ? 'control_operational' : 
                                newTypes.includes('financial') ? 'control_financial' : 'control'
                            });
                          }}
                          className="h-4 w-4 rounded border-stone-300"
                        />
                        <label htmlFor="control_financial" className="text-sm cursor-pointer">
                          <span className="font-medium">Financial Control</span>
                          <span className="text-text-muted ml-1">- Ability to direct financial and operating policies</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <input 
                    type="radio" 
                    id="equity_share_approach" 
                    name="org_boundaries_approach" 
                    value="equity_share"
                    checked={formData.org_boundaries_approach === 'equity_share'}
                    onChange={(e) => setFormData({ ...formData, org_boundaries_approach: e.target.value })}
                    className="mt-1"
                  />
                  <label htmlFor="equity_share_approach" className="text-sm">
                    <span className="font-medium">Equity Share Approach</span>
                    <p className="text-text-muted mt-1">A company considers and accounts for greenhouse gas emissions from various operations according to its share of equity in those operations.</p>
                  </label>
                </div>

                {formData.org_boundaries_approach === 'equity_share' && (
                  <div className="ml-6 border-l-2 border-stone-300 pl-4" data-testid="organization-equity-share-disclaimer">
                    <p className="text-sm text-text-secondary">
                      <strong>Disclaimer:</strong> It is assumed that the data provided corresponds to emissions from the whole facility.
                    </p>
                    <p className="mt-2 text-xs text-text-muted">
                      You can specify the equity share percentage for each facility in the Facilities page.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t border-stone-100">
                <Label className="text-sm">Additional Boundary Notes</Label>
                <textarea 
                  value={formData.org_boundaries} 
                  onChange={(e) => setFormData({ ...formData, org_boundaries: e.target.value })} 
                  rows={2} 
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" 
                  placeholder="Any additional notes on organizational boundaries"
                />
              </div>
            </div>

            {/* Uncertainty Assessment */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Uncertainty Assessment</Label>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>Select the measures taken to minimize uncertainty in your GHG inventory.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-3 p-4 bg-stone-50 rounded-lg border border-stone-200">
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="ua_activity_data"
                    checked={formData.uncertainty_assessment?.includes('activity_data_checked')}
                    onChange={(e) => {
                      const newOptions = e.target.checked 
                        ? [...(formData.uncertainty_assessment || []), 'activity_data_checked']
                        : (formData.uncertainty_assessment || []).filter(o => o !== 'activity_data_checked');
                      setFormData({ ...formData, uncertainty_assessment: newOptions });
                    }}
                    className="mt-1 h-4 w-4 rounded border-stone-300"
                  />
                  <label htmlFor="ua_activity_data" className="text-sm cursor-pointer">
                    The input data has been verified using source documents to avoid errors during entry.
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="ua_inventory_calculations"
                    checked={formData.uncertainty_assessment?.includes('inventory_calculations_checked')}
                    onChange={(e) => {
                      const newOptions = e.target.checked 
                        ? [...(formData.uncertainty_assessment || []), 'inventory_calculations_checked']
                        : (formData.uncertainty_assessment || []).filter(o => o !== 'inventory_calculations_checked');
                      setFormData({ ...formData, uncertainty_assessment: newOptions });
                    }}
                    className="mt-1 h-4 w-4 rounded border-stone-300"
                  />
                  <label htmlFor="ua_inventory_calculations" className="text-sm cursor-pointer">
                    Emission calculations have been checked to ensure data accuracy and consistency across all categories.
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="ua_emission_factors"
                    checked={formData.uncertainty_assessment?.includes('emission_factors_reliable')}
                    onChange={(e) => {
                      const newOptions = e.target.checked 
                        ? [...(formData.uncertainty_assessment || []), 'emission_factors_reliable']
                        : (formData.uncertainty_assessment || []).filter(o => o !== 'emission_factors_reliable');
                      setFormData({ ...formData, uncertainty_assessment: newOptions });
                    }}
                    className="mt-1 h-4 w-4 rounded border-stone-300"
                  />
                  <label htmlFor="ua_emission_factors" className="text-sm cursor-pointer">
                    Emission factors are taken from reliable sources to reduce uncertainty.
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="ua_instruments_calibrated"
                    checked={formData.uncertainty_assessment?.includes('instruments_calibrated')}
                    onChange={(e) => {
                      const newOptions = e.target.checked 
                        ? [...(formData.uncertainty_assessment || []), 'instruments_calibrated']
                        : (formData.uncertainty_assessment || []).filter(o => o !== 'instruments_calibrated');
                      setFormData({ ...formData, uncertainty_assessment: newOptions });
                    }}
                    className="mt-1 h-4 w-4 rounded border-stone-300"
                  />
                  <label htmlFor="ua_instruments_calibrated" className="text-sm cursor-pointer">
                    Measurement instruments and lab equipment are regularly calibrated to ensure accurate results.
                  </label>
                </div>
              </div>
            </div>

            {/* GHG Reduction Initiatives */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>GHG Reduction Initiatives</Label>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>It refers to specific activities or initiatives carried out by the organization, either as one-time actions or ongoing efforts, to reduce or prevent direct and indirect GHG emissions, or to increase the removal of greenhouse gases.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <textarea 
                value={formData.ghg_reduction_initiatives} 
                onChange={(e) => setFormData({ ...formData, ghg_reduction_initiatives: e.target.value })} 
                rows={3} 
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" 
                placeholder="Describe any GHG reduction initiatives undertaken or planned"
              />
            </div>

            {/* Internal Performance Tracking */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Internal Performance Tracking Description</Label>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>It refers to the process of monitoring, measuring, and reviewing greenhouse gas emissions and reduction progress to ensure continuous improvement and effective climate management.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <textarea 
                value={formData.internal_performance_tracking} 
                onChange={(e) => setFormData({ ...formData, internal_performance_tracking: e.target.value })} 
                rows={3} 
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" 
                placeholder="Describe how internal GHG performance is tracked and monitored"
              />
            </div>
            </div>

            <div className={activeTab === 'basic' ? 'space-y-4' : 'hidden'}>
            <div className="space-y-2">
              <Label>Reporting Year Type <span className="text-red-500">*</span></Label>
              <select 
                value={formData.reporting_year_type} 
                onChange={(e) => setFormData({ ...formData, reporting_year_type: e.target.value })} 
                className={`w-full h-10 bg-stone-50 border rounded-lg px-3 ${!formData.reporting_year_type ? 'border-red-300' : 'border-stone-200'}`}
                data-testid="organization-reporting-year-type-select"
              >
                <option value="">Select Year Type</option>
                <option value="financial_year">Financial Year</option>
                <option value="calendar_year">Calendar Year</option>
              </select>
              {!formData.reporting_year_type && (
                <p className="text-xs text-red-500">This field is required</p>
              )}
            </div>
            </div>

            <div className={activeTab === 'basic' ? 'space-y-4' : 'hidden'}>
            {/* Attachments Section */}
            <div className="p-4 border border-stone-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <Paperclip className="w-4 h-4" />
                Attachments
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>You can add organization pictures, satellite images, company policy, website link of the company and any other details related to organization boundary.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {formData.attachments.length > 0 && (
                <div className="space-y-2">
                  {formData.attachments.map((att, idx) => {
                    // Construct proper view and download URLs for uploaded files
                    const isUploadedFile = att.is_draft || (att.url && (att.url.includes('/api/files/') || att.type === 'file'));
                    let viewUrl = att.is_draft ? att.preview_url : (isUploadedFile ? att.url : ensureProtocol(att.url));
                    let downloadUrl = att.url;
                    let uploadedFileId = null;
                    if (isUploadedFile && !att.is_draft) {
                      const fileIdMatch = att.url.match(/\/api\/files\/([^\/]+)/);
                      if (fileIdMatch) {
                        uploadedFileId = fileIdMatch[1];
                        viewUrl = '#';
                      }
                    }
                    return (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                        <Link className="w-4 h-4 text-blue-500" />
                        <span className="flex-1 text-sm truncate">{att.name}</span>
                        <a href={viewUrl} target="_blank" rel="noopener noreferrer" onClick={uploadedFileId ? (event) => { event.preventDefault(); void openSecureFile(uploadedFileId).catch(() => toast.error('Unable to open attachment')); } : undefined} className="text-xs text-blue-600 hover:underline" data-testid={`organization-attachment-view-${idx}`}>View</a>
                        {/* Only show Download for uploaded files, not external links */}
                        {isUploadedFile && !att.is_draft && (
                          <button 
                            type="button"
                            onClick={async (e) => { e.preventDefault(); if (uploadedFileId) { await downloadSecureFile(uploadedFileId); } else { downloadFile(downloadUrl, att.name); } }}
                            className="text-xs text-green-600 hover:underline flex items-center gap-1"
                            data-testid={`organization-attachment-download-${idx}`}
                          >
                            <Download className="w-3 h-3" /> 
                            Download
                          </button>
                        )}
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeAttachment(idx)} data-testid={`organization-attachment-remove-${idx}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Link */}
              <div className="space-y-2">
                <Label className="text-sm">Add Link</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Link Name"
                    value={newAttachment.name}
                    onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                    className="bg-stone-50"
                  />
                  <Input
                    placeholder="URL"
                    value={newAttachment.url}
                    onChange={(e) => setNewAttachment({ ...newAttachment, url: e.target.value })}
                    className="bg-stone-50"
                  />
                  <Button type="button" variant="outline" onClick={addAttachment}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Upload File */}
              <div className="space-y-2">
                <Label className="text-sm">Or Upload Files</Label>
                <div 
                  className="border-2 border-dashed border-stone-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                  onClick={() => document.getElementById('org-file-upload')?.click()}
                >
                  <input
                    id="org-file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    multiple
                    data-testid="organization-attachments-file-input"
                  />
                  <FileText className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                  <p className="text-sm text-text-muted">Drop files here or click to upload</p>
                  <p className="text-xs text-text-muted mt-1">You can select multiple files</p>
                </div>
              </div>
            </div>

            {/* Other Information Section (renamed from Remarks/Notes) */}
            <div className="space-y-2">
              <Label>Other Information</Label>
              <textarea 
                value={formData.other_information} 
                onChange={(e) => setFormData({ ...formData, other_information: e.target.value })} 
                rows={3} 
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" 
                placeholder="Add any additional information about this organization..."
              />
            </div>
            </div>

            {!['production', 'revenue'].includes(activeTab) && (
            <div className="flex justify-between items-center gap-3 pt-4 border-t border-stone-200">
              <AutoSaveStatus 
                status={saveStatus} 
                lastSavedAt={lastSavedAt} 
                errorMessage={errorMessage}
              />
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => { discardFileChanges(); setEditing(false); resetAutoSave(); fetchOrganization(); }} data-testid="cancel-org-btn">Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90 text-white" data-testid="save-org-btn">Save Changes</Button>
              </div>
            </div>
            )}
          </form>
        </Card>
      ) : (
        /* ========== PREMIUM VIEW-ONLY ORGANIZATION PROFILE ========== */
        <div className="flex flex-col gap-4" data-testid="org-view-mode">

          <div className={activeTab === 'basic' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* === QUICK INFO GRID - 2 columns === */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Corporate Address Card */}
            <Card className="p-6 border border-stone-200 rounded-xl bg-white hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 rounded-lg bg-blue-50">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Corporate Address</h3>
              </div>
              <p className="text-sm leading-6 text-text-primary" data-testid="organization-corporate-address">
                {[
                  organization?.corporate_address,
                  organization?.city,
                  organization?.state,
                  organization?.country,
                  organization?.pincode,
                ].filter(Boolean).join(', ') || '—'}
              </p>
            </Card>

            {/* Person Responsible Card */}
            {organization?.person_responsible && (
              <Card className="p-6 border border-stone-200 rounded-xl bg-white hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 rounded-lg bg-purple-50">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-text-primary">Person Responsible</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-lg font-medium text-text-primary">{organization.person_responsible}</p>
                    {organization.person_responsible_designation && (
                      <p className="text-sm text-text-muted flex items-center gap-1 mt-1">
                        <Briefcase className="w-3 h-3" />
                        {organization.person_responsible_designation}
                      </p>
                    )}
                  </div>
                  {organization.person_responsible_contact && (
                    <p className="text-sm text-text-muted flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {organization.person_responsible_contact}
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* === ORGANIZATION OVERVIEW === */}
          {organization?.general_description && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-stone-100">
                  <Building className="w-5 h-5 text-stone-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Organization Overview</h3>
              </div>
              <div className="text-text-secondary leading-relaxed">
                {organization.general_description.length > 400 && !expandedSections.description ? (
                  <>
                    <p>{organization.general_description.slice(0, 400)}...</p>
                    <button 
                      onClick={() => toggleSection('description')} 
                      className="mt-2 text-primary text-sm font-medium flex items-center gap-1 hover:underline"
                    >
                      Read More <ChevronDown className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <p>{organization.general_description}</p>
                    {organization.general_description.length > 400 && (
                      <button 
                        onClick={() => toggleSection('description')} 
                        className="mt-2 text-primary text-sm font-medium flex items-center gap-1 hover:underline"
                      >
                        Show Less <ChevronUp className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </Card>
          )}

          {/* === MISSION & VISION SIDE BY SIDE === */}
          {(organization?.mission || organization?.vision) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {organization?.mission && (
                <Card className="p-6 border border-stone-200 rounded-xl bg-gradient-to-br from-white to-emerald-50/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-text-primary">Mission</h3>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{organization.mission}</p>
                </Card>
              )}
              {organization?.vision && (
                <Card className="p-6 border border-stone-200 rounded-xl bg-gradient-to-br from-white to-blue-50/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-text-primary">Vision</h3>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{organization.vision}</p>
                </Card>
              )}
            </div>
          )}

          {/* === PROCESS DESCRIPTION === */}
          {organization?.process_description && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-orange-50">
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Operations & Process</h3>
              </div>
              <div className="text-text-secondary leading-relaxed">
                {organization.process_description.length > 400 && !expandedSections.process ? (
                  <>
                    <p>{organization.process_description.slice(0, 400)}...</p>
                    <button 
                      onClick={() => toggleSection('process')} 
                      className="mt-2 text-primary text-sm font-medium flex items-center gap-1 hover:underline"
                    >
                      Read More <ChevronDown className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <p>{organization.process_description}</p>
                    {organization.process_description.length > 400 && (
                      <button 
                        onClick={() => toggleSection('process')} 
                        className="mt-2 text-primary text-sm font-medium flex items-center gap-1 hover:underline"
                      >
                        Show Less <ChevronUp className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </Card>
          )}
          </div>

          <div className={activeTab === 'ghg' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* === PURPOSE OF GHG REPORT === */}
          {organization?.report_purpose && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-indigo-50">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Purpose of the GHG Report</h3>
              </div>
              <p className="text-text-secondary leading-relaxed">{organization.report_purpose}</p>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* === ORGANIZATIONAL BOUNDARIES === */}
          {(organization?.org_boundaries_approach || organization?.org_boundaries) && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white" data-testid="organization-ghg-boundaries">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-teal-50">
                  <Shield className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Organizational Boundaries</h3>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        <Info className="w-4 h-4 text-text-muted hover:text-primary transition-colors" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-stone-800 text-white p-3 text-sm">
                      <p>Defines which operations and facilities are included in the GHG inventory based on the selected consolidation approach.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-3">
                {organization.org_boundaries_approach === 'control_operational' && (
                  <div className="rounded-lg border border-stone-200 p-3">
                    <Badge variant="outline" className="mb-2 border-stone-300 bg-transparent text-stone-700">Operational Control</Badge>
                    <p className="text-sm text-text-secondary">The organization accounts for 100% of GHG emissions from operations over which it exercises operational control.</p>
                  </div>
                )}
                {organization.org_boundaries_approach === 'control_financial' && (
                  <div className="rounded-lg border border-stone-200 p-3">
                    <Badge variant="outline" className="mb-2 border-stone-300 bg-transparent text-stone-700">Financial Control</Badge>
                    <p className="text-sm text-text-secondary">The organization accounts for 100% of GHG emissions from operations over which it exercises financial control.</p>
                  </div>
                )}
                {organization.org_boundaries_approach === 'control_both' && (
                  <div className="rounded-lg border border-stone-200 p-3">
                    <Badge variant="outline" className="mb-2 border-stone-300 bg-transparent text-stone-700">Operational & Financial Control</Badge>
                    <p className="text-sm text-text-secondary">The organization accounts for 100% of GHG emissions from operations over which it has both operational and financial control.</p>
                  </div>
                )}
                {organization.org_boundaries_approach === 'control' && (
                  <div className="rounded-lg border border-stone-200 p-3">
                    <Badge variant="outline" className="mb-2 border-stone-300 bg-transparent text-stone-700">Control Approach</Badge>
                    <p className="text-sm text-text-secondary">The organization accounts for 100% of GHG emissions from operations over which it has operational or financial control.</p>
                  </div>
                )}
                {organization.org_boundaries_approach === 'equity_share' && (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-stone-200 p-3">
                      <Badge variant="outline" className="mb-2 border-stone-300 bg-transparent text-stone-700">Equity Share Approach</Badge>
                      <p className="text-sm text-text-secondary">The organization accounts for GHG emissions according to its equity share in each facility.</p>
                    </div>
                    {/* <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded flex items-start gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span><strong>Disclaimer:</strong> It is assumed that the data provided corresponds to emissions from the whole facility.</span>
                    </p> */}
                  </div>
                )}
                {organization.org_boundaries && (
                  <p className="text-sm text-text-secondary mt-2 pt-2 border-t border-stone-100">{organization.org_boundaries}</p>
                )}
              </div>
            </Card>
          )}

          {organization?.uncertainty_assessment?.length > 0 && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white" data-testid="organization-ghg-uncertainty">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-amber-50">
                  <Info className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Uncertainty Assessment</h3>
              </div>
              <ul className="space-y-2 text-sm text-text-secondary list-disc pl-5">
                {organization.uncertainty_assessment.map((assessment) => (
                  <li key={assessment}>{assessment.replaceAll('_', ' ')}</li>
                ))}
              </ul>
            </Card>
          )}
          </div>

          {/* === GHG REDUCTION INITIATIVES - FULL WIDTH === */}
          {organization?.ghg_reduction_initiatives && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-green-50">
                  <Leaf className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-text-primary">GHG Reduction Initiatives</h3>
              </div>
              <p className="text-text-secondary leading-relaxed">{organization.ghg_reduction_initiatives}</p>
            </Card>
          )}

          {/* === INTERNAL PERFORMANCE TRACKING - FULL WIDTH === */}
          {organization?.internal_performance_tracking && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-cyan-50">
                  <BarChart3 className="w-5 h-5 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Internal Performance Tracking</h3>
              </div>
              <p className="text-text-secondary leading-relaxed">{organization.internal_performance_tracking}</p>
            </Card>
          )}
          </div>

          <div className={activeTab === 'basic' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* === ATTACHMENTS === */}
          {organization?.attachments?.length > 0 && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-stone-100">
                  <Paperclip className="w-5 h-5 text-stone-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Attachments</h3>
                <Badge variant="outline" className="ml-auto">{organization.attachments.length} files</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {organization.attachments.map((att, idx) => {
                  const isUploadedFile = att.url && (att.url.includes('/api/files/') || att.type === 'file');
                  let viewUrl = isUploadedFile ? att.url : ensureProtocol(att.url);
                  let downloadUrl = att.url;
                  let uploadedFileId = null;
                  if (isUploadedFile) {
                    const fileIdMatch = att.url.match(/\/api\/files\/([^\/]+)/);
                    if (fileIdMatch) {
                      uploadedFileId = fileIdMatch[1];
                      viewUrl = '#';
                    }
                  }
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group">
                      <div className="p-2 rounded bg-white border border-stone-200">
                        <FileText className="w-4 h-4 text-stone-500" />
                      </div>
                      <span className="flex-1 text-sm font-medium text-text-primary truncate">{att.name}</span>
                      <div className="flex gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <a href={viewUrl} target="_blank" rel="noopener noreferrer" onClick={uploadedFileId ? (event) => { event.preventDefault(); void openSecureFile(uploadedFileId).catch(() => toast.error('Unable to open attachment')); } : undefined} className="text-xs text-primary hover:underline" data-testid={`organization-profile-attachment-view-${idx}`}>View</a>
                        {isUploadedFile && (
                          <button 
                            onClick={async (e) => { e.preventDefault(); if (uploadedFileId) { await downloadSecureFile(uploadedFileId); } else { downloadFile(downloadUrl, att.name); } }}
                            className="text-xs text-emerald-600 hover:underline"
                            data-testid={`organization-profile-attachment-download-${idx}`}
                          >
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* === OTHER INFORMATION === */}
          {(organization?.other_information || organization?.remarks) && (
            <Card className="p-6 border border-stone-200 rounded-xl bg-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-stone-100">
                  <Info className="w-5 h-5 text-stone-600" />
                </div>
                <h3 className="font-semibold text-text-primary">Other Information</h3>
              </div>
              <p className="text-text-secondary leading-relaxed">{organization.other_information || organization.remarks}</p>
            </Card>
          )}
          </div>
        </div>
      )}

        {showOperationalData && (
          <OrganizationOperationalData
            activeTab={activeTab}
            canEdit={canEdit}
            getAuthHeader={getAuthHeader}
            isEditing={editing}
            onCancel={() => { setEditing(false); resetAutoSave(); }}
            organization={organization}
            subscriptionExpired={subscriptionExpired}
          />
        )}
        </div>

      </Tabs>
    </div>
  );
}
